
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useUserImages } from '@/hooks/use-user-images';

interface ImageUploaderProps {
  storeId: string;
  onImageUploaded?: (image: any) => void;
}

export const ImageUploader = ({ storeId, onImageUploaded }: ImageUploaderProps) => {
  const { uploadImage, uploading } = useUserImages(storeId);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    for (const file of acceptedFiles) {
      try {
        const image = await uploadImage(file, 'default', []);
        onImageUploaded?.(image);
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  }, [uploadImage, onImageUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true
  });

  return (
    <Card className="border-dashed border-2">
      <CardContent className="p-6">
        <div
          {...getRootProps()}
          className={`
            cursor-pointer transition-colors duration-200 rounded-lg p-8 text-center
            ${isDragActive ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'}
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          <input {...getInputProps()} />
          
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 rounded-full bg-primary/10">
              <Upload className="h-8 w-8 text-primary" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-lg font-medium">
                {isDragActive ? 'Solte as imagens aqui' : 'Envie suas imagens'}
              </h3>
              <p className="text-muted-foreground text-sm">
                Arraste e solte ou clique para selecionar arquivos
              </p>
              <p className="text-xs text-muted-foreground">
                PNG, JPG, JPEG, SVG, WEBP (máx. 10MB cada)
              </p>
            </div>
            
            {!uploading && (
              <Button variant="outline" size="sm">
                <ImageIcon className="h-4 w-4 mr-2" />
                Selecionar Imagens
              </Button>
            )}
            
            {uploading && (
              <div className="w-full max-w-xs space-y-2">
                <p className="text-sm">Enviando...</p>
                <Progress value={undefined} className="h-2" />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
